import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from './AuthContext';
import { isNativeApp } from '../utils/native';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
const WS_URL = API_URL.replace('http', 'ws').replace('https', 'wss');

function DeploymentNotification() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [countdown, setCountdown] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const [message, setMessage] = useState('');
  const wsRef = useRef(null);
  const hasLoggedOut = useRef(false);

  useEffect(() => {
    if (!user || !user._id) return;

    // Connect to WebSocket
    const connectWebSocket = () => {
      const ws = new WebSocket(`${WS_URL}/api/ws/notifications/${user._id}`);
      
      ws.onopen = () => {
        console.log('✅ WebSocket connected for deployment notifications');
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          console.log('📨 WebSocket message received:', data);
          
          if (data.type === 'deployment') {
            // Show deployment notification
            setMessage(data.message || t('shell.deployment.defaultMessage'));
            setShowNotification(true);
            setCountdown(data.countdown || 10);
          }
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
        }
      };
      
      ws.onerror = (error) => {
        console.error('❌ WebSocket error:', error);
      };
      
      ws.onclose = () => {
        console.log('🔌 WebSocket disconnected, reconnecting in 5s...');
        setTimeout(connectWebSocket, 5000);
      };
      
      wsRef.current = ws;
    };

    connectWebSocket();

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [user]);

  useEffect(() => {
    if (countdown !== null && countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else if (countdown === 0 && !hasLoggedOut.current) {
      // The mobile app keeps its session (device key): just hide the banner.
      if (isNativeApp()) {
        setShowNotification(false);
        setCountdown(null);
        return;
      }
      // Countdown finished - logout and redirect
      hasLoggedOut.current = true;
      
      // Close WebSocket connection
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      
      // Logout and redirect to login page
      logout();
      navigate('/login', { replace: true });
    }
  }, [countdown, logout, navigate]);

  if (!showNotification || countdown === null) {
    return null;
  }

  return (
    <div className="fixed top-4 right-4 z-[9999] animate-slide-in">
      <div className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-6 py-4 rounded-lg shadow-2xl min-w-[350px] border-l-4 border-yellow-600">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0 w-12 h-12 bg-white dark:bg-surface-900 bg-opacity-20 rounded-full flex items-center justify-center text-2xl">
            ⚠️
          </div>
          <div className="flex-1">
            <h3 className="font-bold text-lg mb-1">{message}</h3>
            <p className="text-sm mb-2">{t('shell.deployment.saveYourWork')}</p>
            <div className="flex items-center gap-3">
              <div className="text-3xl font-bold">{countdown}</div>
              <div className="text-sm opacity-90">{t('shell.deployment.secondsUntilUpdate', { count: countdown })}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DeploymentNotification;
